import useIsDesktop from 'hooks/useIsDesktop';
import React, { useContext, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Box,
  Drawer,
} from '@mui/material';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { debounce } from 'lodash-es';
import { Capacitor } from '@capacitor/core';
import useBackHandler from './useBackHandler';
import CloseButton from './CloseButton';
import Header from './Header';
import { OverlayContext } from './constants';

const changeChatVisibility = debounce((visibility) => {
  window?.$crisp?.push(['do', `chat:${visibility}`]);
}, 100);
function Overlay({
  hashKey,
  open = false,
  onClose,
  minHeight,
  mobileFullScreen,
  sx,
  children,
  hideIndicator = false,
  ...props
}) {
  const isDesktop = useIsDesktop();

  useBackHandler({ hashKey, open, onClose });

  useEffect(() => {
    if (!open || window.$crisp?.is?.('chat:hidden')) {
      return;
    }
    changeChatVisibility('hide');

    return () => {
      changeChatVisibility('show');
    };
  }, [open]);

  const contextValue = useMemo(() => ({
    fullScreen: !isDesktop && mobileFullScreen,
    onClose,
    open,
  }), [onClose, open, mobileFullScreen, isDesktop]);

  function renderContent() {
    if (isDesktop) {
      return (
        <Dialog
          {...props}
          sx={{
            ...sx,
            '& .MuiPaper-root': {
              sm: {
                borderRadius: '24px',
                minHeight: minHeight || 200,
              },
              md: {
                width: 640,
                maxWidth: 'unset',
              },
              ...sx?.['& .MuiPaper-root'],
            },

          }}
          onClose={onClose}
          open={open}
        >
          {children}
        </Dialog>
      );
    }

    const DrawerComponent = !mobileFullScreen || Capacitor.getPlatform() !== 'ios' ? SwipeableDrawer : Drawer;

    return (
      <DrawerComponent
        anchor={mobileFullScreen ? 'right' : 'bottom'}
        disableDiscovery
        disableSwipeToOpen
        open={open}
        onClose={onClose}
        onOpen={() => { }}
        {...props}
        sx={{
          ...sx,
          '& .MuiDrawer-paper': {
            height: mobileFullScreen ? '100dvh' : undefined,
            width: mobileFullScreen ? '100vw' : undefined,
            paddingBottom: 'max(var(--gap-bottom) , 8px)',
            minHeight,
            borderRadius: mobileFullScreen ? 0 : undefined,
            overflow: 'visible',
            ...sx?.['& .MuiDrawer-paper'],
          },
          '& .MuiDrawer-paper.MuiDrawer-paperAnchorBottom': mobileFullScreen ? {
            borderRadius: 0,
          } : {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },

        }}
      >
        {
          !mobileFullScreen && !hideIndicator && (
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                top: -28,
                textAlign: 'center',
                p: '10px',
                display: 'flex',
                justifyContent: 'center',
              }}
              className="8989"
            >
              <Box sx={{
                bgcolor: '#fff',
                width: 64,
                height: 8,
                borderRadius: 9,
              }}
              />
            </Box>
          )
        }
        {children}
      </DrawerComponent>
    );
  }

  return (
    <OverlayContext.Provider value={contextValue}>
      {renderContent()}
    </OverlayContext.Provider>
  );
}

Overlay.Header = Header;
Overlay.Content = function Content(props) {
  const { fullScreen } = useContext(OverlayContext);
  return <DialogContent dividers {...props} sx={{ flexBasis: fullScreen ? 0 : undefined, ...props.sx }} />;
};

Overlay.Actions = DialogActions;
Overlay.CloseButton = CloseButton;

export default Overlay;
